import React from 'react';
import PropTypes from 'prop-types';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { POPUP_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { POPUP } from 'utils/constants/popup';
import { assembleRoutePath } from 'functions/assembleRoutePath';
import app from 'new-ui/app';
import scanDate from 'assets/icons/scan_date.svg';
import scheduleACall from 'assets/icons/schedule_a_call.svg';
import updateProfile from 'assets/icons/update_profile.svg';
import shareWithYourDoctor from 'assets/icons/share_with_doctor.svg';
import { formatTextToUseNewLineBeforeNumbers } from '../../functions';
import { CHAT_ROLES } from '../../constants';
import './actionPanel.css';

const shareWithYourDoctorHandler = ({ addMessages, setShowChatNextScan }) => {
  setShowChatNextScan(false);
  const href = assembleRoutePath({
    pathname: ROUTE_PATH[ROUTE_NAME.RESULTS],
    searchParameters: { [POPUP_URL_SEARCH_PARAM]: POPUP.SHARE_WITH_MY_DOCTOR },
  });
  const content = formatTextToUseNewLineBeforeNumbers(`
      We founded this company to provide patients with hope by offering a variety of treatment options. 
      Your oncologist and their clinic are eager to receive these treatment options from you.
      We strongly recommend discussing these options with your doctor.
      Simply click on the following link to send your doctor the treatment options: 
      <a class="link" href="${href}">Share with your doctor</a>`);

  addMessages([{ role: CHAT_ROLES.TINA, content }]);
};

const scheduleACallHandler = ({
  hasSponsoredTrial,
  addMessages,
  setShowChatNextScan,
}) => {
  setShowChatNextScan(false);

  let phraseResponse = 'Our team would love to help you, just schedule a call in the following link: ';
  if (hasSponsoredTrial) {
    const href = assembleRoutePath({
      pathname: ROUTE_PATH[ROUTE_NAME.RESULTS],
      searchParameters: {
        [POPUP_URL_SEARCH_PARAM]: POPUP.CONSULT_WITH_US,
      },
    });
    phraseResponse += ` 
      <br/><a class="link" href="${href}">Schedule a call</a>`;
  } else {
    const href = assembleRoutePath({
      pathname: ROUTE_PATH[ROUTE_NAME.RESULTS],
      searchParameters: { [POPUP_URL_SEARCH_PARAM]: POPUP.SHARE_WITH_MY_DOCTOR },
    });
    phraseResponse += `
      We founded this company to provide patients with hope by offering a variety of treatment options. 
      Your oncologist and their clinic are eager to receive these treatment options from you.
      We strongly recommend discussing these options with your doctor.
      Simply click on the following link to send your doctor the treatment options: 
      <a class="link" href="${href}">Share with your doctor</a>`;
  }
  addMessages([{
    role: CHAT_ROLES.TINA,
    content: formatTextToUseNewLineBeforeNumbers(phraseResponse),
  }]);
};

const handleChatActionButtonClicked = ({ onClick, key }) => {
  const clickedButton = document.querySelector(`[data-key="${key}"]`);
  if (clickedButton) {
    clickedButton.classList.add('chat-active-action-panel-button');
  }

  const otherButtons = document.querySelectorAll('.chat-actions-panel-button');
  if (otherButtons) {
    otherButtons.forEach((button) => {
      if (button !== clickedButton) {
        button.classList.remove('chat-active-action-panel-button');
      }
    });
  }

  onClick();
};

export const ActionPanel = ({
  loading, hasSponsoredTrial, addMessages, setShowChatNextScan,
}) => {
  const chatActionsButtons = [
    {
      key: 'update_scan_date',
      icon: scanDate,
      alt: 'Update Scan Date',
      label: 'Update Scan Date',
      onClick: () => {
        setShowChatNextScan(true);
        addMessages([{
          role: CHAT_ROLES.TINA,
          content: 'Please share with us your last scan date',
        }]);
      },
    },
    {
      key: 'share_with_doctor',
      icon: shareWithYourDoctor,
      alt: 'Share with your doctor',
      label: 'Share with your doctor',
      onClick: () => shareWithYourDoctorHandler({ addMessages, setShowChatNextScan }),
    },
    hasSponsoredTrial ? {
      key: 'schedule_call',
      icon: scheduleACall,
      alt: 'Schedule A Call',
      label: 'Schedule A Call',
      onClick: () => scheduleACallHandler({
        hasSponsoredTrial,
        addMessages,
        setShowChatNextScan,
      }),
    } : {
      key: 'update_profile',
      icon: updateProfile,
      alt: 'Update profile',
      label: 'Update profile',
      onClick: () => app.setPopup(POPUP.MEDICAL_PROFILE),
    },
  ];

  return (
    <div className="chat-actions-panel-fullmode">
      <div className="chat-actions-panel-buttons">
        {chatActionsButtons.map(({
          onClick, key, icon, alt, label,
        }) => {
          return (
            <button
              key={key}
              data-key={key}
              type="button"
              disabled={loading}
              title={alt}
              className="chat-actions-panel-button"
              onClick={() => {
                handleChatActionButtonClicked({ onClick, key });
              }}
            >
              <img className="chat-actions-panel-icon" src={icon} alt={alt} />
              <br />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

ActionPanel.propTypes = {
  hasSponsoredTrial: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  addMessages: PropTypes.func.isRequired,
  setShowChatNextScan: PropTypes.func.isRequired,
};
